/**
 * Presentation only. Every value arriving here is already final — the server
 * did the arithmetic. These functions decide how it looks, never what it is.
 */

const money0 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const money2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

export const fmtMoney = (v) => money0.format(Number(v ?? 0))
export const fmtMoneyExact = (v) => money2.format(Number(v ?? 0))

export function fmtDate(iso) {
  if (!iso) return ''
  const [y, m, d] = String(iso).slice(0, 10).split('-')
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

export function fmtMonth(month) {
  if (!month) return ''
  const [y, m] = month.split('-')
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Cap states never rely on colour alone: each carries a label and a distinct
 * bar treatment, so the screen is readable in sunlight and to a red-green
 * colour-blind reader.
 *
 * Red is real dollars already spent — 95%+ of the cap gone — not a forecast.
 * Orange is the forecast: pace/one-time spend on track to clear the cap by
 * month's end even though actual spend hasn't hit red yet. Yellow is an
 * earlier heads-up (pace at 80%+). See compute_state/handler.py's
 * _state_for for the exact thresholds.
 */
export const STATE_META = {
  red: { label: 'Over', text: 'text-over', bg: 'bg-over', ring: 'ring-over/30' },
  orange: { label: 'Trending over', text: 'text-trend', bg: 'bg-trend', ring: 'ring-trend/30' },
  yellow: { label: 'Close', text: 'text-warn', bg: 'bg-warn', ring: 'ring-warn/30' },
  green: { label: 'OK', text: 'text-ok', bg: 'bg-ok', ring: 'ring-ok/20' },
  none: { label: '', text: 'text-neutral-400', bg: 'bg-neutral-300', ring: 'ring-neutral-300/40' },
}

export const stateMeta = (state) => STATE_META[state] ?? STATE_META.none

/** Fraction of cap used, clamped for rendering a bar. Never used as a number. */
export function capFraction(value, cap) {
  if (!cap || Number(cap) <= 0) return 0
  return Math.max(0, Math.min(1.25, Number(value ?? 0) / Number(cap)))
}
